import { useState, useEffect } from 'react';
import { productService, categoryService, FirestoreProduct, FirestoreCategory } from '../services/firestore';

// Products hook
export const useProducts = () => {
  const [products, setProducts] = useState<FirestoreProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedProducts = await productService.getAll();
      setProducts(fetchedProducts);
    } catch (err: any) {
      setError(err.message || 'Ürünler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const addProduct = async (product: Omit<FirestoreProduct, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const id = await productService.create(product);
      await fetchProducts(); // Listeyi yenile
      return id;
    } catch (err: any) {
      setError(err.message || 'Ürün eklenirken hata oluştu');
      throw err;
    }
  };

  const updateProduct = async (id: string, updates: Partial<Omit<FirestoreProduct, 'id' | 'createdAt'>>) => {
    try {
      await productService.update(id, updates);
      await fetchProducts(); // Listeyi yenile
    } catch (err: any) {
      setError(err.message || 'Ürün güncellenirken hata oluştu');
      throw err;
    }
  };

  const deleteProduct = async (id: string) => {
    try {
      await productService.delete(id);
      await fetchProducts(); // Listeyi yenile
    } catch (err: any) {
      setError(err.message || 'Ürün silinirken hata oluştu');
      throw err;
    }
  };

  return {
    products,
    loading,
    error,
    refetch: fetchProducts,
    addProduct,
    updateProduct,
    deleteProduct
  };
};

// Categories hook
export const useCategories = () => {
  const [categories, setCategories] = useState<FirestoreCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedCategories = await categoryService.getAll();
      setCategories(fetchedCategories);
    } catch (err: any) {
      setError(err.message || 'Kategoriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const addCategory = async (category: Omit<FirestoreCategory, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const id = await categoryService.create(category);
      await fetchCategories(); // Listeyi yenile
      return id;
    } catch (err: any) {
      setError(err.message || 'Kategori eklenirken hata oluştu');
      throw err;
    }
  };

  const updateCategory = async (id: string, updates: Partial<Omit<FirestoreCategory, 'id' | 'createdAt'>>) => {
    try {
      await categoryService.update(id, updates);
      await fetchCategories(); // Listeyi yenile
    } catch (err: any) {
      setError(err.message || 'Kategori güncellenirken hata oluştu');
      throw err;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      await categoryService.delete(id);
      await fetchCategories(); // Listeyi yenile
    } catch (err: any) {
      setError(err.message || 'Kategori silinirken hata oluştu');
      throw err;
    }
  };

  return {
    categories,
    loading,
    error,
    refetch: fetchCategories,
    addCategory,
    updateCategory,
    deleteCategory
  };
};

// Public products hook (mevcut frontend için)
export const usePublicProducts = (categorySlug?: string) => {
  const [products, setProducts] = useState<FirestoreProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let fetchedProducts: FirestoreProduct[];
        if (categorySlug) {
          fetchedProducts = await productService.getByCategory(categorySlug);
        } else {
          fetchedProducts = await productService.getAll();
        }
        
        fetchedProducts.forEach((product, index) => {
        });
        
        setProducts(fetchedProducts);
      } catch (err: any) {
        console.error('❌ Ürün yükleme hatası:', err);
        setError(err.message || 'Ürünler yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categorySlug]); 

  return { products, loading, error };
};

// Public categories hook (mevcut frontend için)
export const usePublicCategories = () => {
  const [categories, setCategories] = useState<FirestoreCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedCategories = await categoryService.getAll();
        setCategories(fetchedCategories);
      } catch (err: any) {
        setError(err.message || 'Kategoriler yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return { categories, loading, error };
};

// Tekli kategori hook (slug ile kategori bilgisi almak için)
export const usePublicCategory = (slug?: string) => {
  const [category, setCategory] = useState<FirestoreCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setCategory(null);
      setLoading(false);
      return;
    }

    const fetchCategory = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedCategory = await categoryService.getBySlug(slug);
        setCategory(fetchedCategory);
      } catch (err: any) {
        console.error('❌ Kategori yükleme hatası:', err);
        setError(err.message || 'Kategori yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [slug]);

  return { category, loading, error };
};